import Logger from '@utils/log'
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import {
    ROLE_PARSE_ADMIN,
    DOCUMENT_STATUS_MODEL_APPLIED,
    DOCUMENT_STATUS_READY_FOR_REVIEW,
    DOCUMENT_STATUS_REVIEW_IN_PROGRESS,
    DOCUMENT_STATUS_REVIEWED,
    DOCUMENT_TYPES,
} from '@constants'
import useUserHasRoles from '@hook/useUserHasRoles'
import { useFetchDocument } from './DocumentView.hooks'
import { useCallbackRef } from '@hook/useCallbackRef'
import { useRenderPdf } from '@hook/useRenderPdf'
import { useFullScreen } from '@hook/useFullScreen'
import { buildRecordUrl, omit } from '@utils'

const log = new Logger('useDocumentViewViewModel')

export const VIEW_MODES = {
    NONE: 0,
    PDF: 1,
    PNG: 2,
}

export const validReviewStatuses = [
    DOCUMENT_STATUS_MODEL_APPLIED,
    DOCUMENT_STATUS_READY_FOR_REVIEW,
    DOCUMENT_STATUS_REVIEW_IN_PROGRESS,
    DOCUMENT_STATUS_REVIEWED,
]

const useDocumentViewViewModel = () => {
    
    const refCanvas = useRef()
    const refContainer = useRef()
    const { documentId } = useParams()
    const navigate = useNavigate()
    
    const [refEditor, editorNode, clearElement] = useCallbackRef()
    
    const [error, setError] = useState(null)
    const [record, setRecord] = useState(null)
    const [isEditMode, setIsEditMode] = useState(true)
    const [loadingCapture, setLoadingCapture] = useState(false)
    const [pageIndex, setPageIndex] = useState(0)
    const [pageCount, setPageCount] = useState(0)
    const [editDisabledHelpDialogOpen, setEditDisabledHelpDialogOpen] = useState(false)
    
    const collection = useWireValue(store.currentCollection)
    const [showAllLabelsChecked, setShowAllLabelsChecked] = useWireState(store.labelStudioShowAllLabelsChecked)
    const [showPrivateLabelsChecked, setShowPrivateLabelsChecked] = useWireState(
        store.adminLabelStudioShowPrivateLabelsChecked)
    const [showExpandedAnnotationView, setShowExpandedAnnotationView] = useWireState(store.showExpandedAnnotationView)
    
    const isParseAdmin = useUserHasRoles(ROLE_PARSE_ADMIN)
    
    const pdfUrl = useMemo(() => buildRecordUrl(record, true, record?.collection?.isParse), [record])
    
    const [documentType, setDocumentType] = useState({ id: '', label: '' })
    
    const viewMode = useMemo(() => (
        record?.fileName?.endsWith('.pdf')
            ? VIEW_MODES.PDF
            : record?.pages?.length > 0
                ? VIEW_MODES.PNG
                : VIEW_MODES.NONE
    ), [record])
    
    const documentPageUrls = useMemo(() => (
        record?.pages?.map(it => buildRecordUrl(it, false, record.collection?.isParse))
    ), [record])
    
    const imageUrl = useMemo(() => (
        documentPageUrls?.[pageIndex ?? 0] ?? null
    ), [documentPageUrls, pageIndex])
    
    const canEditDocument = useMemo(() => {
        return validReviewStatuses.includes(record?.reviewStatus)
    }, [record])
    
    
    const { isFullScreen, toggleFullScreen } = useFullScreen(refContainer)
    
    const onRenderPdfError = useCallback(err => {
        
        log.e('onRenderPdfError', err)
        
        switch (err.name) {
            case 'MissingPDFException':
                setError('Document not found')
                break
            default:
                setError('An unexpected error occurred')
        }
        
    }, [])
    
    useRenderPdf({
        enabled: !isEditMode && viewMode === VIEW_MODES.PDF,
        url: pdfUrl,
        pageIndex,
        pageCount,
        setPageCount,
        getCanvas: () => refCanvas.current,
        onError: onRenderPdfError,
    })
    
    useFetchDocument(
        documentId,
        data => setRecord(data),
        () => navigate('/collections'),
    )
    
    useEffect(() => {
        
        // When we exit edit mode, we need to clear the useCallbackRef
        // element, since removing/adding it to the DOM will change its ref
        if (!isEditMode)
            clearElement()
        
    }, [clearElement, isEditMode])
    
    useEffect(() => {
        
        if (!record) return
        
        log.v('setPageCount?', viewMode === VIEW_MODES.PNG, '=>', record.pages?.length ?? 0)
        
        if (viewMode === VIEW_MODES.PNG)
            setPageCount(record.pages?.length ?? 0)
        
    }, [record, viewMode])
    
    useEffect(() => {
        if (record?.documentType)
            setDocumentType(DOCUMENT_TYPES.find(l => l.id === record?.documentType))
        
    }, [record?.documentType])
    
    const toggleAnnotationCompleted = async documentId => {
        const doc = await actions.toggleAnnotationCompleted(documentId)
        
        if (doc)
            setRecord(prev => ({ ...prev, annotationCompleted: doc.annotationCompleted }))
    }
    
    const captureBboxText = async () => {
        const annotation = window.app?.ls?.store?.annotationStore?.annotations?.[0]
        const serialized = annotation?.serializeAnnotation()
        
        if (serialized) {
            const coords = serialized
                .filter(s => s.from_name === 'bbox')
                .map(s => ({ id: s.id, ...omit(s.value, [ 'x', 'y', 'width', 'height'], true) }))
            
            setLoadingCapture(true)
            
            const data = await actions.captureTextsFromBBox(record?.pages[pageIndex]?.id, coords)
            
            data.forEach(d => {
                const annotation = serialized.find(s => s.id === d.annotationId && s.from_name === 'transcription')
                
                // this is just a sanity check, it should always find
                if (annotation)
                    annotation.value.text = [d.result.text]
            })
            
            annotation.deleteAllRegions()
            annotation.deserializeResults(serialized)
            
            setLoadingCapture(false)
        }
    }
    
    
    
    return {
        
        refCanvas,
        refContainer,
        refEditor,
        
        error,
        editorNode,
        collection,
        record,
        setRecord,
        imageUrl,
        isEditMode,
        loadingCapture,
        setIsEditMode,
        pageIndex,
        setPageIndex,
        pageCount,
        setPageCount,
        
        isFullScreen,
        toggleFullScreen,
        viewMode,
        isParseAdmin,
        pdfUrl,
        documentPageUrls,
        canEditDocument,
        documentType,
        setDocumentType,
        
        showAllLabelsChecked,
        setShowAllLabelsChecked,
        showPrivateLabelsChecked,
        setShowPrivateLabelsChecked,
        editDisabledHelpDialogOpen,
        setEditDisabledHelpDialogOpen,
        showExpandedAnnotationView,
        setShowExpandedAnnotationView,
        
        toggleAnnotationCompleted,
        
        captureBboxText,
    }
    
}

export default useDocumentViewViewModel
